/**
 * CR313: V2 Preview Deal Contract Validator
 *
 * Validates that a deal object has the minimum fields required
 * for deterministic V2 rendering. Used by the PreviewContractDebugPanel.
 */

export interface V2ContractField {
  key: string;
  label: string;
  value: unknown;
  present: boolean;
  required: boolean;
  group: "identity" | "value" | "dates";
}

export interface V2ContractValidation {
  ok: boolean;
  missing: string[];
  fields: V2ContractField[];
}

function has(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === "string") return v.length > 0;
  if (typeof v === "number") return !isNaN(v);
  return true;
}

/**
 * Validate a deal object against the V2 Preview Deal Contract.
 * Accepts any shape (UIDeal, raw DB row, etc.)
 */
export function validateV2RenderDeal(deal: Record<string, unknown> | null): V2ContractValidation {
  if (!deal) {
    return {
      ok: false,
      missing: ["deal_object"],
      fields: [],
    };
  }

  const cdj = (deal.canonical_deal_json ?? {}) as Record<string, unknown>;
  const cdjValue = (cdj.value ?? {}) as Record<string, unknown>;
  const cdjDates = (cdj.dates ?? {}) as Record<string, unknown>;

  const fields: V2ContractField[] = [
    // Identity
    { key: "id", label: "Deal ID", value: deal.id ?? deal._previewId, present: has(deal.id) || has(deal._previewId), required: true, group: "identity" },
    { key: "dedupe_key", label: "Dedupe Key", value: deal.dedupe_key, present: has(deal.dedupe_key), required: false, group: "identity" },
    { key: "deal_type", label: "Deal Type", value: deal.deal_category ?? deal.deal_type ?? cdj.deal_type, present: has(deal.deal_category) || has(deal.deal_type) || has(cdj.deal_type), required: true, group: "identity" },

    // Value fields
    { key: "computed_value_cad", label: "Computed Value", value: deal.computed_value_cad ?? cdjValue.computed_value_cad, present: has(deal.computed_value_cad) || has(cdjValue.computed_value_cad), required: false, group: "value" },
    { key: "discount_amount_cad", label: "Discount Amount", value: deal.discount_amount_cad ?? cdjValue.discount_amount_cad ?? cdjValue.discount_value_cad, present: has(deal.discount_amount_cad) || has(cdjValue.discount_amount_cad) || has(cdjValue.discount_value_cad), required: false, group: "value" },
    { key: "qualifying_spend_cad", label: "Qualifying Spend", value: deal.qualifying_spend_cad ?? cdjValue.qualifying_spend_cad, present: has(deal.qualifying_spend_cad) || has(cdjValue.qualifying_spend_cad), required: false, group: "value" },
    { key: "effective_percent_back", label: "Effective %", value: deal.effective_percent_back ?? cdjValue.effective_percent_back, present: has(deal.effective_percent_back) || has(cdjValue.effective_percent_back), required: false, group: "value" },

    // Dates
    { key: "start_date", label: "Start Date", value: deal.start_date ?? cdjDates.valid_from, present: has(deal.start_date) || has(cdjDates.valid_from), required: false, group: "dates" },
    { key: "end_date", label: "End Date", value: deal.end_date ?? cdjDates.valid_until, present: has(deal.end_date) || has(cdjDates.valid_until), required: false, group: "dates" },
  ];

  // Value sufficiency: at least one value field present
  const hasAnyValue = fields.filter(f => f.group === "value").some(f => f.present);

  const missing = fields
    .filter(f => f.required && !f.present)
    .map(f => f.key);

  if (!hasAnyValue) {
    missing.push("any_value_field");
  }

  return {
    ok: missing.length === 0,
    missing,
    fields,
  };
}
